import React from "react"
import { CloseIcon, IconButton, Stack, useTheme, Typography, Box } from "@carrotfertility/carotene-core"
import { MultiPathSVG } from "#/components/views/SVG"
import AttachmentStates from "#/utils/AttachmentStates"
import { AttachmentType } from "#/components/get-reimbursed/steps/UploadFile"
import { FormattedMessage, useIntl } from "react-intl"
import Settings from "../../../../utils/CarrotConfig"

interface AttachmentHelperTextProps {
  attachmentState: AttachmentStates
  handleRetryClick: () => void
  fileName: string
}

interface UploadFileProps {
  attachment: AttachmentType
  onRetry: (attachmentGuid: string) => void
  onDelete: (attachmentGuid: string) => void
}

const isAttachmentError = (attachment: AttachmentType): boolean =>
  [
    AttachmentStates.OVERSIZED,
    AttachmentStates.FAILED,
    AttachmentStates.INVALIDTYPE,
    AttachmentStates.VIRUSDETECTED
  ].includes(attachment.state)

const FailedStateLabel = ({ handleRetryClick }: Pick<AttachmentHelperTextProps, "handleRetryClick">) => {
  const theme = useTheme()
  return (
    <Stack direction="row" spacing={theme.tokens.spacing.xs}>
      <FormattedMessage defaultMessage="Upload failed" />
      <a style={{ color: "inherit" }} href="#/" onClick={handleRetryClick}>
        <FormattedMessage defaultMessage="RETRY?" />
      </a>
    </Stack>
  )
}

const UploadingLabel = () => (
  <>
    <Box
      component="span"
      sx={(theme) => ({
        display: "inline-flex",
        verticalAlign: "middle",
        marginInlineEnd: theme.tokens.spacing.xs
      })}
    >
      <MultiPathSVG iconName="icn-uploading-animated" />
    </Box>
    <FormattedMessage defaultMessage="Uploading" />
  </>
)

function AttachmentHelperText(props: AttachmentHelperTextProps) {
  const fileType = /\.\w+$/.exec(props.fileName)
  const maxFileSize = Settings.MAX_FILE_SIZE / 1000000
  switch (props?.attachmentState) {
    case AttachmentStates.UPLOADING:
      return <UploadingLabel />
    case AttachmentStates.COMPLETED:
      return <FormattedMessage defaultMessage="Upload complete" />
    case AttachmentStates.FAILED:
      return <FailedStateLabel handleRetryClick={props?.handleRetryClick} />
    case AttachmentStates.OVERSIZED:
      return (
        <FormattedMessage defaultMessage="FILE TOO LARGE. MAX FILE SIZE IS {maxFileSize} MB" values={{ maxFileSize }} />
      )
    case AttachmentStates.INVALIDTYPE:
      return (
        <FormattedMessage defaultMessage="Invalid file type {fileType} files are not supported" values={{ fileType }} />
      )
    case AttachmentStates.VIRUSDETECTED:
      return <FormattedMessage defaultMessage="Upload failed - corrupt file" />
    default:
      return <></>
  }
}

const UploadFile = ({ attachment, onRetry, onDelete }: UploadFileProps): JSX.Element => {
  const theme = useTheme()
  const { file } = attachment
  const attachmentError = isAttachmentError(attachment)
  const intl = useIntl()

  const handleDeleteClick = React.useCallback(() => {
    onDelete(attachment.guid)
  }, [attachment.guid, onDelete])

  const handleRetryClick = React.useCallback(() => {
    onRetry(attachment.guid)
  }, [attachment.guid, onRetry])

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography
          fontWeight="bold"
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {file?.name}
        </Typography>
        <IconButton
          size="small"
          color="secondary"
          aria-label={intl.formatMessage({ defaultMessage: "Remove upload" })}
          onClick={handleDeleteClick}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Typography variant="body2" color={attachmentError ? theme.palette.error.main : theme.palette.text.disabled}>
        <AttachmentHelperText
          attachmentState={attachment?.state}
          handleRetryClick={handleRetryClick}
          fileName={file?.name}
        />
      </Typography>
    </Box>
  )
}

export { UploadFile }
